import { v4 as uuidv4 } from "uuid";
import FeatureSwitchCheck from "./FeatureSwitchCheck";
import { UserType } from "types/Users";


function getStoragePrefix(featureSwitch: string): string {
  switch (featureSwitch) {
    case "REACT_APP_PRICING_TABLE_SHOW_FREE_PLAN":
      return "sa_pricing_ab";
    default:
      return `sa_${uuidv4()}`;
  }
}

interface PercentageRolloutConfig {
  percentage: number;
  emails: string[];
}

export class PercentageRollout extends FeatureSwitchCheck<PercentageRolloutConfig | undefined, [ UserType | null | undefined ]> {

  protected parseFeatureValue(featureSwitchValue: string | undefined): PercentageRolloutConfig | undefined {
    if (!featureSwitchValue) {
      return undefined;
    }

    // Split by comma and trim whitespace
    const parts = featureSwitchValue.split(',').map(part => part.trim());

    if (parts.length === 0) {
      return undefined;
    }

    // First part should be the percentage (just a number like 25, 50, 100)
    const percentage = Number.parseFloat(parts[0]);

    if (isNaN(percentage)) {
      return undefined;
    }

    // Remaining parts are emails (if any)
    const emails = parts.slice(1).filter(email => email.length > 0);

    return {
      percentage,
      emails
    };
  }

  protected doCheck(featureValue: PercentageRolloutConfig | undefined, user: UserType | null | undefined): boolean {
    // If no config is set, disable the feature
    if (!featureValue) {
      return false;
    }

    if (!user) {
      return false;
    }

    // Check if user is in the allowed emails list (before percentage calculation)
    if (featureValue.emails.length > 0 && featureValue.emails.includes(user.email)) {
      return true;
    }

    // If percentage is invalid or 0, disable for non-listed users
    if (featureValue.percentage <= 0) {
      return false;
    }

    // If percentage is 100 or more, enable for everyone
    if (featureValue.percentage >= 100) {
      return true;
    }

    // Check localStorage for existing decision
    const userHash = (user.id * 9301 + 49297) % 233280;
    const storageKey = `${getStoragePrefix(this.featureSwitch)}_${userHash}`;
    const storedDecision = localStorage.getItem(storageKey);

    if (storedDecision !== null) {
      return storedDecision === 'true';
    }

    // Make a new random decision
    const randomValue = Math.random() * 100;
    const isEnabled = randomValue < featureValue.percentage;

    // Store the decision
    localStorage.setItem(storageKey, String(isEnabled));

    return isEnabled;
  }

}
